/*
 * Maison des Nus — <sticky-header> custom element.
 * Dawn ships StickyHeader inline in sections/header.liquid; our oi-header replaced that
 * section, so the element was left UNDEFINED (header was inert). This restores it as a
 * standalone asset and keeps Dawn's public contract:
 *   - toggles .shopify-section-header-sticky / .shopify-section-header-hidden / .animate
 *     on the section wrapper (.section-header) — same classes Dawn/OI CSS keys off.
 *   - exposes reveal() — cart-notification.js calls document.querySelector('sticky-header').reveal().
 *   - honours the 'preventHeaderReveal' event (media-gallery.js / header menus dispatch it).
 *   - keeps --header-height in sync via a single source of truth.
 * Visual timing (translateY(-100%), .3s ease-in-out, z-index 9999) lives in oi-motion.css.
 */
if (!customElements.get('sticky-header')) {
  class StickyHeader extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      // The section wrapper carries .section-header (added via oi-header schema "class").
      // Fall back to this element's closest .shopify-section so it still works if the
      // class is ever missing.
      this.header =
        document.querySelector('.section-header') || this.closest('.shopify-section') || this;
      this.headerIsAlwaysSticky =
        this.getAttribute('data-sticky-type') === 'always' ||
        this.getAttribute('data-sticky-type') === 'reduce-logo-size';
      this.headerBounds = {};

      this.setHeaderHeight();
      window
        .matchMedia('(max-width: 990px)')
        .addEventListener('change', this.setHeaderHeight.bind(this));

      if (this.headerIsAlwaysSticky) {
        this.header.classList.add('shopify-section-header-sticky');
      }

      this.currentScrollTop = 0;
      this.preventReveal = false;
      this.predictiveSearch = this.querySelector('predictive-search');

      this.onScrollHandler = this.onScroll.bind(this);
      this.hideHeaderOnScrollUp = () => (this.preventReveal = true);

      this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.addEventListener('scroll', this.onScrollHandler, false);

      this.createObserver();
    }

    setHeaderHeight() {
      document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
      this.setHeaderBottom();
    }

    // --header-bottom: the header's live bottom edge in the viewport (announcement bar included
    // while visible, 0 when the header is scrolled/hidden away). The mobile filter drawer pins to
    // this so it opens flush under the header instead of overlapping it (--header-height alone is
    // just the bar's own 58px and knows nothing about the announcement above it).
    setHeaderBottom() {
      const bottom = Math.max(0, Math.round(this.header.getBoundingClientRect().bottom));
      if (bottom !== this._lastHeaderBottom) {
        this._lastHeaderBottom = bottom;
        document.documentElement.style.setProperty('--header-bottom', `${bottom}px`);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.removeEventListener('scroll', this.onScrollHandler);
    }

    createObserver() {
      let observer = new IntersectionObserver((entries, observer) => {
        this.headerBounds = entries[0].intersectionRect;
        observer.disconnect();
      });

      observer.observe(this.header);
    }

    onScroll() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

      this.setHeaderBottom();

      if (this.predictiveSearch && this.predictiveSearch.isOpen) return;

      if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        this.header.classList.add('scrolled-past-header');
        if (this.preventHide) return;
        requestAnimationFrame(this.hide.bind(this));
      } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        this.header.classList.add('scrolled-past-header');
        if (!this.preventReveal) {
          requestAnimationFrame(this.reveal.bind(this));
        } else {
          window.clearTimeout(this.isScrolling);
          this.isScrolling = setTimeout(() => {
            this.preventReveal = false;
          }, 66);
          requestAnimationFrame(this.hide.bind(this));
        }
      } else if (scrollTop <= this.headerBounds.top) {
        this.header.classList.remove('scrolled-past-header');
        requestAnimationFrame(this.reset.bind(this));
      }

      this.currentScrollTop = scrollTop;
    }

    hide() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
      this.closeMenuDisclosure();
      this.closeSearchModal();
    }

    reveal() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.add('shopify-section-header-sticky', 'animate');
      this.header.classList.remove('shopify-section-header-hidden');
    }

    reset() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.remove(
        'shopify-section-header-hidden',
        'shopify-section-header-sticky',
        'animate'
      );
    }

    closeMenuDisclosure() {
      // OI header uses native <details> dropdowns, not <header-menu>. Close any that are open
      // so an expanded dropdown doesn't ride the header up off-screen.
      this.disclosures =
        this.disclosures || this.header.querySelectorAll('details[open] > summary');
      this.disclosures = this.header.querySelectorAll('details[open]');
      this.disclosures.forEach((details) => details.removeAttribute('open'));
    }

    closeSearchModal() {
      this.searchModal = this.searchModal || this.header.querySelector('details-modal');
      if (this.searchModal && typeof this.searchModal.close === 'function') {
        this.searchModal.close(false);
      }
    }
  }

  customElements.define('sticky-header', StickyHeader);
}
